import React from 'react'
import { Dialog } from '@headlessui/react'
import { X, Tag, Trash2 } from 'lucide-react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { RootState, AppDispatch } from '../../app/store'
import {
  closeModal,
  updateCodeController,
  setDiscountData,
  clearDiscount,
  setLoading,
  setError,
  clearError
} from '../../features/discount/discountSlice'
import { useValidateCouponMutation, useApplyCouponWithValidationMutation, useRemoveCouponMutation } from '../../features/cart/cartAPI'
import { loadCartItems, selectCartItems, selectCartInfo, selectTotalPrice } from '../../features/cart/cartItemsSlice'
import {
  refreshCsCartItems,
  selectCsCartItems,
  selectCsCartInfo,
  selectCsCartTotalPrice
} from '../../features/csFlow/csCartSlice'
import { isCustomerServiceUser } from '../../utils/apiUtils'
import { useCurrencyTextLabel } from '../../hooks/useCurrencyTextLabel'

interface DiscountModalProps {
  isOpen: boolean
  onClose: () => void
  isRTL?: boolean
}

const DiscountModal: React.FC<DiscountModalProps> = ({
  isOpen,
  onClose,
  isRTL = true,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const currencyLabel = useCurrencyTextLabel()

  const {
    hasDiscount,
    discountCode,
    discountAmount,
    codeController,
    isLoading,
    error
  } = useSelector((state: RootState) => state.discount)

  const { hasCustomerData, customerId } = useSelector((state: RootState) => state.customer)
  const user = useSelector((state: RootState) => state.auth.user)
  const cartItemsRaw = useSelector(selectCartItems)
  const cartInfo = useSelector(selectCartInfo)
  const cartTotalPrice = useSelector(selectTotalPrice)
  const csCartItems = useSelector(selectCsCartItems)
  const csCartInfo = useSelector(selectCsCartInfo)
  const csCartTotalPrice = useSelector(selectCsCartTotalPrice)
  const csCustomerId = useSelector((state: RootState) => state.csFlow.customerId)
  const isCustomerService = React.useMemo(() => isCustomerServiceUser(), [])
  const activeCartItems = isCustomerService ? csCartItems : cartItemsRaw
  const activeCartInfo = isCustomerService ? csCartInfo : cartInfo
  const activeCartTotalPrice = isCustomerService ? csCartTotalPrice : cartTotalPrice

  // Get userId - use customer ID if available, otherwise use cashier ID
  const getUserId = (): number => {
    if (hasCustomerData && customerId) {
      return customerId
    }
    // Fallback to cashier ID
    try {
      const userAny = user as any
      if (userAny?.user_id) {
        return Number(userAny.user_id)
      }
      if (userAny?.user?.id) {
        return Number(userAny.user.id)
      }
    } catch (e) {
      // Ignore
    }
    // Try localStorage
    try {
      const userDataStr = localStorage.getItem('user_data')
      if (userDataStr) {
        const userData = JSON.parse(userDataStr)
        if (userData.user_id) {
          return Number(userData.user_id)
        }
        if (userData.user?.id) {
          return Number(userData.user.id)
        }
      }
      const employeeDataStr = localStorage.getItem('employee_data')
      if (employeeDataStr) {
        const employeeData = JSON.parse(employeeDataStr)
        if (employeeData.user_id) {
          return Number(employeeData.user_id)
        }
        if (employeeData.user?.id) {
          return Number(employeeData.user.id)
        }
      }
    } catch (e) {
      // Ignore
    }
    return 7 // Default fallback
  }

  // RTK Query hooks
  const [validateCoupon] = useValidateCouponMutation()
  const [applyCouponWithValidation] = useApplyCouponWithValidationMutation()
  const [removeCoupon] = useRemoveCouponMutation()

  const handleCloseModal = () => {
    dispatch(closeModal())
    dispatch(clearError())
    onClose()
  }

  const handleCodeChange = (value: string) => {
    // Keep the original case as the customer types it
    dispatch(updateCodeController(value))
  }

  const handleApplyDiscount = async () => {
    if (!codeController.trim()) {
      dispatch(setError(t('discount.code_required') || 'يرجى إدخال كود الخصم'))
      return
    }

    // Check if cart has items
    if (!activeCartItems || activeCartItems.length === 0) {
      dispatch(setError(t('discount.cart_empty') || 'السلة فارغة'))
      return
    }

    try {
      dispatch(setLoading(true))
      dispatch(clearError())

      const userId = getUserId()
      const tableId = activeCartInfo?.table_id ? Number(activeCartInfo.table_id) : undefined

      // Get cart total amount for validation
      // Use total_price from cartInfo, or fallback to selector
      const amount = activeCartInfo?.total_price
        ? parseFloat(activeCartInfo.total_price.toString())
        : activeCartTotalPrice

      // Step 1: Validate coupon first (matches Flutter's checkCouponCode flow)
      let isCouponValid = false
      try {
        const validationResponse = await validateCoupon({
          couponCode: codeController.trim(),
          amount: amount,
          userId: userId,
        }).unwrap()

        // Check if coupon is valid (matches Flutter's _isValidCouponResponse logic)
        const responseData = validationResponse
        const hasSuccess = responseData?.success === true
        const hasSuccessMessage = responseData?.message?.toString().includes('success') === true ||
          responseData?.message?.toString().includes('Apis/messages.success') === true
        const hasDiscount = responseData?.discount != null

        isCouponValid = hasSuccess || (hasSuccessMessage && hasDiscount)
      } catch (validationError: any) {
        // Handle validation errors (matches Flutter's error handling)
        if (validationError?.status === 422 || validationError?.status === 404 || validationError?.status === 400) {
          // Invalid coupon - check error response
          const errorData = validationError?.data
          const message = errorData?.message?.toString() || ''
          const errors = errorData?.errors

          // Check if it's an invalid coupon error (not a server error)
          const isInvalidCoupon = message.includes('invalid') ||
            message.includes('The selected code is invalid') ||
            message.includes('غير صالح') ||
            (errors?.code != null && Array.isArray(errors.code) && errors.code.length > 0)

          if (isInvalidCoupon) {
            isCouponValid = false
          } else {
            // Re-throw if it's a different error
            throw validationError
          }
        } else {
          // Re-throw other errors
          throw validationError
        }
      }

      // Step 2: If coupon is valid, apply it to cart
      if (isCouponValid) {
        const result = await applyCouponWithValidation({
          userId,
          couponCode: codeController.trim(),
          tableId,
        }).unwrap()

        // unwrapResponse already extracts data from {message, data} wrapper
        // So result should be the cart data directly
        if (result) {
          // Get discount amount from API response
          // The API returns discount as a number in the cart data
          const discountAmount = parseFloat(result.discount?.toString() || '0')

          if (discountAmount > 0) {
            // Update discount data
            dispatch(setDiscountData({
              code: codeController.trim(),
              amount: discountAmount,
              percentage: 0 // API doesn't return percentage, calculate if needed
            }))

            // Reload cart items to reflect the updated prices
            if (isCustomerService && csCustomerId > 0) {
              await dispatch(refreshCsCartItems({
                customerId: csCustomerId,
                forceRefresh: true,
              }) as any)
            } else {
              await dispatch(loadCartItems({
                forceRefresh: true,
                couponCode: codeController.trim(),
                tableId,
              }) as any)
            }

            handleCloseModal()
          } else {
            // Valid coupon but no discount applied
            dispatch(setError(t('discount.invalid_code') || 'كود الخصم غير صحيح'))
          }
        } else {
          dispatch(setError(t('discount.invalid_code') || 'كود الخصم غير صحيح'))
        }
      } else {
        // Step 3: Coupon is invalid, show error message
        dispatch(setError(t('discount.invalid_code') || 'كود الخصم غير صحيح'))
      }
    } catch (error: any) {
      // Handle errors with user-friendly messages (matches Flutter's error handling)
      let userFriendlyMessage = t('discount.error_general') || 'حدث خطأ في تطبيق كود الخصم'

      if (error?.status === 422) {
        userFriendlyMessage = t('discount.error_invalid_expired') || 'كود الخصم غير صالح أو منتهي الصلاحية'
      } else if (error?.status === 404) {
        userFriendlyMessage = t('discount.error_not_found') || 'كود الخصم غير موجود'
      } else if (error?.status === 400) {
        userFriendlyMessage = t('discount.error_incorrect') || 'كود الخصم غير صحيح'
      } else if (error?.status === 401) {
        userFriendlyMessage = t('discount.error_login_required') || 'يجب تسجيل الدخول'
      } else if (error?.status === 500) {
        userFriendlyMessage = t('discount.error_server') || 'خطأ في الخادم'
      } else if (error?.message?.includes('422')) {
        userFriendlyMessage = t('discount.error_invalid_expired') || 'كود الخصم غير صالح أو منتهي الصلاحية'
      } else if (error?.message?.includes('expired') || error?.message?.includes('منتهي')) {
        userFriendlyMessage = t('discount.error_expired') || 'كود الخصم منتهي الصلاحية'
      } else if (error?.message?.includes('invalid') || error?.message?.includes('غير صالح')) {
        userFriendlyMessage = t('discount.error_invalid') || 'كود الخصم غير صالح'
      } else {
        userFriendlyMessage = error?.data?.message || error?.message || userFriendlyMessage
      }

      dispatch(setError(userFriendlyMessage))
    } finally {
      dispatch(setLoading(false))
    }
  }

  const handleRemoveDiscount = async () => {
    try {
      dispatch(setLoading(true))

      const userId = getUserId()
      const tableId = activeCartInfo?.table_id ? Number(activeCartInfo.table_id) : undefined

      // Remove coupon using cart API
      await removeCoupon({
        userId,
        tableId,
      }).unwrap()

      // Clear before reloading: cart loaders fall back to the Redux discount code
      dispatch(clearDiscount())

      // Reload cart items to reflect the updated prices
      if (isCustomerService && csCustomerId > 0) {
        await dispatch(refreshCsCartItems({
          customerId: csCustomerId,
          forceRefresh: true,
          couponCode: '' // Empty coupon code to remove discount
        }) as any)
      } else {
        await dispatch(loadCartItems({
          forceRefresh: true,
          tableId,
        }) as any)
      }

      handleCloseModal()
    } catch (error: any) {
      // Still clear locally even if API fails
      dispatch(clearDiscount())
      // Reload cart items anyway
      try {
        const tableId = activeCartInfo?.table_id ? Number(activeCartInfo.table_id) : undefined
        if (isCustomerService && csCustomerId > 0) {
          await dispatch(refreshCsCartItems({
            customerId: csCustomerId,
            forceRefresh: true,
            couponCode: '' // Empty coupon code to remove discount
          }) as any)
        } else {
          await dispatch(loadCartItems({
            forceRefresh: true,
            tableId,
          }) as any)
        }
      } catch (e) {
        // Ignore
      }
      handleCloseModal()
    } finally {
      dispatch(setLoading(false))
    }
  }

  const formatPrice = (price: number) => {
    return price.toFixed(2)
  }

  return (
    <Dialog open={isOpen} onClose={handleCloseModal} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel
          className={`w-full max-w-[480px] bg-white rounded-3xl overflow-hidden shadow-2xl ${isRTL ? 'text-right' : 'text-left'
            }`}
        >
          {/* Header */}
          <div className={`flex items-center justify-between gap-4 px-4 py-4 border-b border-gray-100 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Dialog.Title className={`flex-1 text-xl font-medium text-flutter-black font-['IBM Plex Sans Arabic'] ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('discount.apply_discount') || 'كود الخصم'}
            </Dialog.Title>
            <button
              onClick={handleCloseModal}
              className="w-9 h-9 rounded-xl bg-white border border-gray-200 shadow-sm flex items-center justify-center hover:bg-gray-50 transition-colors"
              aria-label={t('common.close') || 'إغلاق'}
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 pt-2 pb-4 space-y-4">
            {!hasDiscount ? (
              <>
                {/* Code Input */}
                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-2 font-['IBM Plex Sans Arabic'] ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('discount.discount_code') || 'كود الخصم'}
                  </label>
                  <input
                    type="text"
                    value={codeController}
                    onChange={(e) => handleCodeChange(e.target.value)}
                    placeholder={t('discount.enter_code') || 'ادخل كود الخصم'}
                    className={`
                      w-full px-4 py-3 border border-gray-300 rounded-xl
                      focus:ring-2 focus:ring-flutter-button focus:border-flutter-button
                      text-base font-['IBM Plex Sans Arabic'] text-flutter-black
                      placeholder-gray-500 outline-none
                      ${isRTL ? 'text-right' : 'text-left'}
                    `}
                    maxLength={20}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                    <p className={`text-sm text-red-600 font-['IBM Plex Sans Arabic'] ${isRTL ? 'text-right' : 'text-left'}`}>
                      {error}
                    </p>
                  </div>
                )}

                {/* Apply Button */}
                <button
                  onClick={handleApplyDiscount}
                  disabled={!codeController.trim() || isLoading}
                  className={`
                    w-full h-12 px-6 rounded-xl font-semibold text-white font-['IBM Plex Sans Arabic']
                    transition-all duration-200
                    ${!codeController.trim() || isLoading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-flutter-button hover:bg-orange-600 active:scale-[0.99]'
                    }
                  `}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>{t('discount.applying') || 'جاري التطبيق...'}</span>
                    </div>
                  ) : (
                    <span>{t('discount.apply') || 'تحقق'}</span>
                  )}
                </button>
              </>
            ) : (
              <>
                {/* Applied Discount Display */}
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                        <Tag className="h-5 w-5 text-green-600" />
                      </div>
                      <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
                        <p className="text-base font-semibold text-flutter-black font-['IBM Plex Sans Arabic']">
                          {discountCode}
                        </p>
                        <p className="text-sm text-green-600 font-['IBM Plex Sans Arabic']">
                          -{formatPrice(discountAmount)} {currencyLabel}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleRemoveDiscount}
                      disabled={isLoading}
                      className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center hover:bg-red-200 transition-colors duration-200"
                    >
                      <Trash2 className="h-5 w-5 text-red-600" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export default DiscountModal



